import { Sentiments } from '@sede-enterprise/shell-ds-fabrics';
import { getNextValidAvailabilitySp } from '@see-power-pft-common-js/availability/utils';
import dayjs from 'dayjs';
import { FormikProps } from 'formik';
import {
  AvailabilityFormDrawerEnabledFields,
  NewAvailabilityEntry,
  NewSiteAvailabilityEntry,
} from 'pages/Availability/common/FormDrawer/types';
import { AvailabilityReason } from 'pages/Availability/types';

export interface AvailabilityFormRulesOptions {
  disableOpenEnded?: boolean;
}

export function buildAvailabilityFormRules(
  formik: FormikProps<NewSiteAvailabilityEntry>,
  enabled: AvailabilityFormDrawerEnabledFields,
  { disableOpenEnded = false }: AvailabilityFormRulesOptions = {},
) {
  const handlers = {
    start: (value: string | null) => {
      formik.setFieldValue('start', value);

      // RULE: If end date empty or repeat daily is enabled, align end time to start time (keep only time component of end)
      if ((formik.values.repeatDaily || !formik.values.end) && value) {
        const start = dayjs(value);
        const alignedEnd = start
          .hour(start.hour())
          .minute(start.minute())
          .second(0)
          .millisecond(0);

        formik.setFieldValue('end', alignedEnd.toISOString());
      }
    },

    end: (value: string | null) => {
      formik.setFieldValue('end', value);
    },

    startImmediately: (value: boolean) => {
      formik.setFieldValue('startImmediately', value);

      // RULE: If starting immediately, set start to next settlement period
      if (value) {
        const nextSP = dayjs(getNextValidAvailabilitySp(new Date()))
          .second(0)
          .millisecond(0);
        formik.setFieldValue('start', nextSP.toISOString());
      }
    },

    repeatDaily: (value: boolean) => {
      formik.setFieldValue('repeatDaily', value);

      // RULE: Repeat daily cannot be open-ended
      formik.setFieldValue('isOpenEnded', false);

      // RULE: If not repeating daily, clear repeatEndInclusive
      if (!value) {
        formik.setFieldValue('repeatEndInclusive', null);
      }
    },

    repeatEndInclusive: (value: string | null) => {
      const cleanValue = value
        ? dayjs(value).set('second', 0).millisecond(0)
        : null;
      formik.setFieldValue(
        'repeatEndInclusive',
        cleanValue?.toISOString() ?? null,
      );
    },

    isOpenEnded: (value: boolean) => {
      formik.setFieldValue('isOpenEnded', value);

      // RULE: If open-ended, clear the end date
      if (value) {
        formik.setFieldValue('end', null);
      }
    },

    availabilityReason: (value: any) => {
      formik.setFieldValue('availabilityReason', value);
    },

    notes: (value: string) => {
      formik.setFieldValue('notes', value);
    },

    maxExportPower: (value: number | string | null) => {
      formik.setFieldValue('maxExportPower', value);
    },

    maxImportPower: (value: number | string | null) => {
      formik.setFieldValue('maxImportPower', value);
    },

    energyCapacity: (value: number | string | null) => {
      formik.setFieldValue('energyCapacity', value);
    },
  };

  const helper = (field: string) => {
    const key = field as keyof NewAvailabilityEntry<AvailabilityReason>;
    return formik.errors[key] && formik.touched[key]
      ? {
          content: formik.errors[key] as string,
          sentiment: Sentiments.Negative,
        }
      : undefined;
  };

  // Comprehensive enablement map for all fields & sub-aspects
  const fieldEnablement = {
    startDateEnabled: enabled.start && !formik.values.startImmediately,
    startTimeEnabled: enabled.start && !formik.values.startImmediately,
    endDateEnabled: enabled.end && !formik.values.isOpenEnded,
    endTimeEnabled: enabled.end || formik.values.isOpenEnded,
    startImmediatelyEnabled: enabled.startImmediately,
    repeatDailyEnabled: enabled.repeatDaily,
    repeatEndInclusiveEnabled:
      enabled.repeatEndInclusive && formik.values.repeatDaily,
    openEndedEnabled:
      enabled.isOpenEnded && !disableOpenEnded && !formik.values.repeatDaily,
    availabilityReasonEnabled: enabled.availabilityReason,
    notesEnabled: enabled.notes,
    maxExportPowerEnabled: enabled.maxExportPower,
    maxImportPowerEnabled: enabled.maxImportPower,
    energyCapacityEnabled: enabled.energyCapacity,
  };

  return { handlers, helper, fieldEnablement };
}
